import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import dayjs from 'dayjs';

import { BookingTypes, TaskCategory, TaskStatus } from 'api';
import {
  confirmBookingPhotographerMutation,
  confirmPersonBookingMutation,
  getProposalApi,
  markTaskAsDonePartiallyMutation,
  updateBookingTypeMutation,
} from 'api/bookings/query';
import { bookPhotographerSchema } from 'components/v2/division/form/schema/createOrderSchema';
import { getBookingInfoByOrderIdApi, getOrderTasksApi } from 'api/orders/query';
import { createNewOrderStore } from 'stores/createNewOrderStore';
import { MODALS_STORE, TASKS_MODAL_ID } from 'constants/ui/modal';
import { userStore } from 'stores/userStore';
import Modal from 'components/v2/modal/Modal';
import { MODAL_WIDTH } from 'constants/ui/style';
import BookingPhotographerModalContent from './BookingPhotographerModalContent';
import { getCaseApi } from 'api/cases/query';
import { useLocalization } from 'localization';
import TaskModalTitle from 'components/v2/shared/TaskModalTitle';

const BookingPhotographerModal = () => {
  const { t } = useLocalization();

  const { toggleModal, isModalOpen, getModalData } = MODALS_STORE.tasks();

  const orderId = getModalData(TASKS_MODAL_ID.BOOK_PHOTOGRAPHER)?.task?.orderId;
  const caseId = getModalData(TASKS_MODAL_ID.BOOK_PHOTOGRAPHER)?.task?.caseId;
  const taskId = getModalData(TASKS_MODAL_ID.BOOK_PHOTOGRAPHER)?.task?.id;
  const taskStatus = getModalData(TASKS_MODAL_ID.BOOK_PHOTOGRAPHER)?.task?.status;
  const caseReference = getModalData(TASKS_MODAL_ID.BOOK_PHOTOGRAPHER)?.task?.caseReference;
  const { user } = userStore();
  const { formData } = createNewOrderStore();

  const { data } = getOrderTasksApi(orderId ?? formData?.orderId);
  const { data: bookingData, isLoading: isBookingDataLoading } = getBookingInfoByOrderIdApi(
    orderId ?? formData?.orderId
  );

  const { data: proposalData, isLoading: isProposalLoading, isFetching } = getProposalApi(orderId ?? formData?.orderId);

  //this line is to get the contact person of the current case because
  //users can interact with this modal in the tasks screen
  const { data: contactPersonData, isLoading } = getCaseApi(caseId ?? formData?.case?.id);

  const { mutateAsync, isLoading: isUpdateBookingLoading } = updateBookingTypeMutation();
  const { mutateAsync: mutateBookingPhotographerAsync, isLoading: isBookingPhotographerLoading } =
    confirmBookingPhotographerMutation();
  const { mutateAsync: mutateBookingPersonAsync, isLoading: isBookingPersonLoading } = confirmPersonBookingMutation();
  const { mutateAsync: mutateMarkDoneAsync, isLoading: isMarkDoneLoading } = markTaskAsDonePartiallyMutation();

  const methods = useForm({
    defaultValues: {
      selectedDate: undefined,
      selectedTime: undefined,
      socialMediaType: '',
      slideShowMediaType: '',
      bookingType: BookingTypes.BookingSecondaryConfirmed,
      inputInstructions: '',
      proposalId: '',
      contactPerson: { label: '', value: '' },
    },
    resolver: yupResolver(bookPhotographerSchema()),
  });

  const { getValues, setValue, watch, handleSubmit } = methods;

  const isProposalIdValid = !watch('proposalId') && watch('bookingType') === BookingTypes.BookingSecondaryConfirmed;
  const loadingState =
    isUpdateBookingLoading ||
    isBookingPhotographerLoading ||
    isBookingPersonLoading ||
    isMarkDoneLoading ||
    isProposalLoading ||
    isBookingDataLoading ||
    isFetching;

  useEffect(() => {
    //not able to use values from useForm because it caused
    //infinite loops, so I decided to use useEffect for this temporarily
    const initialDate = dayjs(proposalData?.primary?.arrival);
    const initialTime = initialDate.hour(initialDate.hour()).minute(0);
    setValue('selectedDate', initialDate);
    setValue('selectedTime', initialTime);
    setValue('proposalId', proposalData?.primary?.id ?? '');
  }, [isModalOpen(TASKS_MODAL_ID.BOOK_PHOTOGRAPHER), proposalData]);

  const onSubmitBooking = async () => {
    const values = getValues();

    const getBoookingPhotographerTaskId = data?.find(task => task.type === TaskCategory.BookPhotographer)?.id ?? '';
    const getContactPerson = contactPersonData?.contactPersons?.find(
      person => person?.name?.toLocaleLowerCase() === values?.contactPerson?.label?.toLocaleLowerCase()
    );

    await mutateAsync({
      bookingId: orderId ?? bookingData?.bookingId,
      taskId: taskId ?? getBoookingPhotographerTaskId,
      changeToType: values.bookingType,
      person: formData?.contactPersons?.[0]?.name ?? contactPersonData?.contactPersons?.[0]?.name,
      responsibleUser: user?.fullName ?? '',
    });

    switch (values.bookingType) {
      case BookingTypes.BookingSecondaryConfirmed:
        await mutateBookingPhotographerAsync({
          bookingId: orderId ?? bookingData?.bookingId,
          proposalId: values?.proposalId ?? '',
        });
        break;
      case BookingTypes.Booking3rdParty:
        await mutateBookingPersonAsync({
          caseId: caseId ?? formData?.case?.id,
          realtorName: user?.fullName ?? '',
          recipientEmail: getContactPerson?.contactInfo?.email ?? '',
          recipientName: getContactPerson?.name ?? '',
          recipientPhone: getContactPerson?.contactInfo?.phonePrivate ?? getContactPerson?.contactInfo?.phoneWork ?? '',
          taskId: taskId ?? getBoookingPhotographerTaskId,
        });
        await mutateMarkDoneAsync(getBoookingPhotographerTaskId);
        break;
      case BookingTypes.BookingEsoft:
        await mutateMarkDoneAsync(getBoookingPhotographerTaskId);
        break;
      default:
        break;
    }

    toggleModal(TASKS_MODAL_ID.BOOK_PHOTOGRAPHER);
  };

  return (
    <FormProvider {...methods}>
      <Modal
        isOpen={isModalOpen(TASKS_MODAL_ID.BOOK_PHOTOGRAPHER)}
        toggleModal={() => {
          toggleModal(TASKS_MODAL_ID.BOOK_PHOTOGRAPHER);
          setValue('bookingType', BookingTypes.BookingSecondaryConfirmed);
        }}
        modalTitle={<TaskModalTitle title={t('modals.beforeAfter.overviewStepTitle')} caseId={caseReference ?? ''} />}
        width={MODAL_WIDTH.xxl}
        isLoading={loadingState}
        onConfirm={handleSubmit(onSubmitBooking)}
        disableConfirm={isProposalIdValid}
        hideFooter={taskStatus === TaskStatus.Complete}
      >
        <BookingPhotographerModalContent
          loading={isProposalLoading || isFetching || isLoading}
          proposals={proposalData}
          currentCaseData={contactPersonData}
          disabled={taskStatus === TaskStatus.Complete}
        />
      </Modal>
    </FormProvider>
  );
};

export default BookingPhotographerModal;
